// src/containers/BookingRequest/components/BookingActions.tsx
import React, { useState } from "react";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { IoMdCloseCircleOutline } from "react-icons/io";
import {
  CheckCircleIcon,
  XCircleIcon,
  DocumentTextIcon,
  TrashIcon,
  EyeIcon,
  EyeSlashIcon,
  HomeIcon,
  ArrowDownTrayIcon,
} from "@heroicons/react/24/outline";
import downloadReceipt from "utils/helpers/downloadReceipt";

interface BookingActionsProps {
  bookingStatus: string;
  isHost: boolean;
  invoiceId?: string | number | null;
  propertySlug?: string;
  showInvoice: boolean;
  showUserInvoice: boolean;
  isDeleting: boolean;
  onToggleInvoice: () => void;
  onToggleUserInvoice: () => void;
  onDeleteInvoice: () => void;
  onCancelBooking: () => void;
  onOpenReview?: () => void;
  isReviewEnabled?: boolean;
  hasReview?: boolean;
  // Receipt download — only needed when status is confirmed
  bookingId?: number | string | null;
  bookingNumber?: string | null;
}

const BookingActions: React.FC<BookingActionsProps> = ({
  bookingStatus,
  isHost,
  invoiceId,
  propertySlug,
  showInvoice,
  showUserInvoice,
  isDeleting,
  onToggleInvoice,
  onToggleUserInvoice,
  onDeleteInvoice,
  onCancelBooking,
  onOpenReview,
  isReviewEnabled = false,
  hasReview = false,
  bookingId,
  bookingNumber,
}) => {
  const [downloading, setDownloading] = useState(false);

  const handleDownloadReceipt = async () => {
    if (!bookingId || !bookingNumber) return;
    setDownloading(true);
    try {
      await downloadReceipt(Number(bookingId), bookingNumber);
    } catch (err) {
      console.error("Receipt download failed:", err);
    } finally {
      setDownloading(false);
    }
  };

  // ── Canceled ──────────────────────────────────────────────────────────────
  if (bookingStatus === "canceled") {
    return (
      <div className="bg-red-50 dark:bg-red-900/10 rounded-xl p-4 sm:p-5 border border-red-200 dark:border-red-800">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center flex-shrink-0">
            <IoMdCloseCircleOutline className="w-6 h-6 text-red-600 dark:text-red-400" />
          </div>
          <div>
            <h4 className="text-base font-bold text-red-700 dark:text-red-300">
              Request Declined
            </h4>
            <p className="text-xs text-red-600 dark:text-red-400 mt-0.5">
              This booking request has been canceled
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Confirmed ─────────────────────────────────────────────────────────────
  if (bookingStatus === "confirmed") {
    return (
      <div className="space-y-3">
        {/* Confirmed Status Card */}
        <div className="bg-green-50 dark:bg-green-900/10 rounded-xl p-4 sm:p-5 border border-green-200 dark:border-green-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center flex-shrink-0">
              <CheckCircleIcon className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <h4 className="text-base font-bold text-green-700 dark:text-green-300">
                Booking Confirmed!
              </h4>
              <p className="text-xs text-green-600 dark:text-green-400 mt-0.5">
                Your booking has been successfully confirmed
              </p>
            </div>
          </div>
        </div>

        {/* Download Receipt Button — available to both host and guest */}
        {bookingId && bookingNumber && (
          <div className="flex justify-center">
            <button
              onClick={handleDownloadReceipt}
              disabled={downloading}
              className={`
                inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold
                transition-all duration-200 border
                ${
                  downloading
                    ? "bg-gray-100 dark:bg-gray-800 text-gray-400 border-gray-200 dark:border-gray-700 cursor-not-allowed"
                    : "bg-white dark:bg-gray-900 text-[#F56948] border-[#F56948] hover:bg-[#FFF5F2] dark:hover:bg-[#F56948]/10 shadow-sm hover:shadow-md active:scale-95"
                }
              `}
            >
              {downloading ? (
                <>
                  <svg
                    className="animate-spin h-4 w-4 text-[#F56948]"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <circle
                      className="opacity-25"
                      cx="12" cy="12" r="10"
                      stroke="currentColor" strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  <span>Generating PDF…</span>
                </>
              ) : (
                <>
                  <ArrowDownTrayIcon className="w-4 h-4" />
                  <span>Download Receipt</span>
                </>
              )}
            </button>
          </div>
        )}

        {/* Student Review Button */}
        {!isHost && onOpenReview && (
          <div className="flex justify-center">
            <button
              onClick={onOpenReview}
              disabled={!isReviewEnabled || hasReview}
              className={`
                inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold
                transition-all duration-200
                ${
                  !isReviewEnabled || hasReview
                    ? "bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                    : "bg-[#F56948] hover:bg-[#E5512F] text-white shadow-md hover:shadow-lg active:scale-95"
                }
              `}
            >
              {hasReview ? (
                <>
                  <CheckCircleIcon className="w-4 h-4" />
                  <span>Review Submitted</span>
                </>
              ) : isReviewEnabled ? (
                <>
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                  <span>Post Review</span>
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 opacity-70" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-xs sm:text-sm font-bold tracking-wider uppercase">
                    Review After Your Stay
                  </span>
                </>
              )}
            </button>
          </div>
        )}
      </div>
    );
  }

  // ── Host Actions ───────────────────────────────────────────────────────────
  if (isHost) {
    return (
      <div className="space-y-3">
        {bookingStatus === "invoice_sent" ? (
          <>
            <div className="bg-blue-50 dark:bg-blue-900/10 rounded-xl p-4 border border-blue-200 dark:border-blue-800">
              <div className="flex items-center gap-2">
                <DocumentTextIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                <div>
                  <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-300">
                    Invoice Issued
                  </h4>
                  <p className="text-xs text-blue-700 dark:text-blue-400 mt-0.5">
                    Waiting for guest payment
                  </p>
                </div>
              </div>
            </div>

            {invoiceId != null && (
              <div className="flex justify-center">
                <button
                  onClick={onDeleteInvoice}
                  disabled={isDeleting}
                  className={`
                    inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold
                    transition-all duration-200
                    ${
                      isDeleting
                        ? "bg-gray-300 dark:bg-gray-700 text-gray-500 cursor-not-allowed"
                        : "bg-red-500 hover:bg-red-600 text-white shadow-md hover:shadow-lg active:scale-95"
                    }
                  `}
                >
                  {isDeleting ? (
                    <>
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      <span>Deleting...</span>
                    </>
                  ) : (
                    <>
                      <TrashIcon className="w-4 h-4" />
                      <span>Delete Invoice</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="flex justify-center">
            <button
              onClick={onToggleInvoice}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold
                       bg-[#F56948] hover:bg-[#E5512F] text-white shadow-md hover:shadow-lg 
                       transition-all duration-200 active:scale-95"
            >
              {showInvoice ? (
                <>
                  <EyeSlashIcon className="w-4 h-4" />
                  <span>Close Invoice</span>
                </>
              ) : (
                <>
                  <DocumentTextIcon className="w-4 h-4" />
                  <span>Issue Invoice</span>
                </>
              )}
            </button>
          </div>
        )}

        <div className="flex justify-center pt-2">
          <button
            onClick={onCancelBooking}
            disabled={bookingStatus === "canceled"}
            className={`
              inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold
              transition-all duration-200
              ${
                bookingStatus === "canceled"
                  ? "bg-gray-200 dark:bg-gray-700 text-gray-500 cursor-not-allowed"
                  : "bg-red-500 hover:bg-red-600 text-white shadow-md hover:shadow-lg active:scale-95"
              }
            `}
          >
            <XCircleIcon className="w-4 h-4" />
            <span>Reject Request</span>
          </button>
        </div>
      </div>
    );
  }

  // ── Student/Guest Actions ──────────────────────────────────────────────────
  return (
    <div className="space-y-3">
      {bookingStatus === "invoice_sent" ? (
        <div className="flex justify-center">
          <button
            onClick={onToggleUserInvoice}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-bold
                     bg-green-500 hover:bg-green-600 text-white shadow-md hover:shadow-lg 
                     transition-all duration-200 active:scale-95"
          >
            {showUserInvoice ? (
              <>
                <EyeSlashIcon className="w-5 h-5" />
                <span>Hide Invoice</span>
              </>
            ) : (
              <>
                <DocumentTextIcon className="w-5 h-5" />
                <span>Check &amp; Pay Invoice</span>
              </>
            )}
          </button>
        </div>
      ) : (
        <div className="bg-yellow-50 dark:bg-yellow-900/10 rounded-xl p-4 border border-yellow-200 dark:border-yellow-800">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center flex-shrink-0">
              <svg className="w-4 h-4 text-yellow-600 dark:text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
              </svg>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-yellow-900 dark:text-yellow-300">
                Request Pending
              </h4>
              <p className="text-xs text-yellow-700 dark:text-yellow-400 mt-0.5">
                Waiting for host to issue invoice
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap justify-center gap-2 pt-2">
        <button
          onClick={onCancelBooking}
          disabled={bookingStatus === "canceled"}
          className={`
            inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold
            transition-all duration-200
            ${
              bookingStatus === "canceled"
                ? "bg-gray-200 dark:bg-gray-700 text-gray-500 cursor-not-allowed"
                : "bg-red-500 hover:bg-red-600 text-white shadow-md hover:shadow-lg active:scale-95"
            }
          `}
        >
          <XCircleIcon className="w-4 h-4" />
          <span>Cancel Request</span>
        </button>

        {bookingStatus !== "canceled" && propertySlug && (
          <ButtonPrimary
            href={`/listing-stay-detail/${propertySlug}`}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold
                     bg-[#F56948] hover:bg-[#E5512F] text-white shadow-md hover:shadow-lg 
                     transition-all duration-200 active:scale-95"
          >
            <HomeIcon className="w-4 h-4" />
            <span>View Property</span>
          </ButtonPrimary>
        )}
      </div>
    </div>
  );
};

export default BookingActions;